import { FC } from 'react';

import { useRecoilValue } from 'recoil';

import { EnquiryType } from '@shared/api/enquiry/enquiry.interface';
import { Button } from '@shared/components/Button';
import { EnquiryResponseTimeNotice } from '@shared/components/EnquiryResponseTimeNotice';
import { FileUploader } from '@shared/components/FileUploader';
import { LoadingModal } from '@shared/components/LoadingModal';
import TelField from '@shared/components/TelField';
import { TextField } from '@shared/components/TextField';
import { EnquiryInputs, IEnquiryInputs } from '@shared/interfaces/enquiry.interface';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { checkFilesSize } from '@shared/utils/checkFilesSize';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  ENQUIRY_FILE_TOO_LARGE_MESSAGE,
  ENQUIRY_FILES_SIZE_LIMIT,
  useCreateEnquiry,
  useSeedEnquiryContact,
} from './useCreateEnquiry';

const ALLOWED_FILE_FORMAT = 'image/*';

export interface EnquiryFormProps {
  //* 관리자 문의사항 관리의 탭(부고/스토어/셀러)을 가르는 값
  type: EnquiryType;
  //* 접수 성공 후 — 콜백이 있으면 그것(패널이 내역 탭으로 전환), 없으면 경로 이동
  onSuccess?: () => void;
  successTo?: string;
  //* 콘솔마다 상단 네비가 제목을 대신하는 곳은 숨긴다
  showTitle?: boolean;
  afterQuickHelp?: boolean;
  className?: string;
}

/**
 * 1:1 문의 폼 — 부고·화환 스토어·답례품 배송관리자·화환 판매자 콘솔이 같은 폼을 쓴다.
 * 콘솔별로 다른 건 문의 유형(type)과 접수 후 돌아갈 곳뿐이다.
 */
export const EnquiryForm: FC<EnquiryFormProps> = ({
  type,
  onSuccess,
  successTo = '/',
  showTitle = true,
  afterQuickHelp = false,
  className = 'p-4',
}) => {
  const { userProfile } = useAuth();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const navigate = useNavigate();
  const { submit, isLoading: processingEnquiry } = useCreateEnquiry(type);

  const { register, handleSubmit, control, setValue, getValues } =
    useForm<IEnquiryInputs>();

  const telValue = useWatch({ control, name: EnquiryInputs.Tel, defaultValue: '' });

  //* 로그인 계정이 있으면 성함·연락처를 미리 채운다(1회 시딩·빈 칸만)
  useSeedEnquiryContact(userProfile, ({ name, phone }) => {
    if (name && !getValues(EnquiryInputs.Name)) setValue(EnquiryInputs.Name, name);
    if (phone && !getValues(EnquiryInputs.Tel)) setValue(EnquiryInputs.Tel, phone);
  });

  const onSubmit: SubmitHandler<IEnquiryInputs> = async (data) => {
    const isAllowed = checkFilesSize(data.files, ENQUIRY_FILES_SIZE_LIMIT);
    if (!isAllowed) {
      // 파일 크기가 초과됨.
      notificateError(notifiacationInstance, ENQUIRY_FILE_TOO_LARGE_MESSAGE);
      return;
    }
    const ok = await submit({
      name: data.name,
      phoneNumber: data.tel,
      title: data.title,
      content: data.description,
      file: data.files[0] ?? null,
    });
    if (!ok) return;
    if (onSuccess) onSuccess();
    else navigate(successTo);
  };

  return (
    <>
      <div className={className}>
        {showTitle && <h1 className="py-8 text-center text-xl font-bold">1:1 문의</h1>}
        <div
          className={
            afterQuickHelp
              ? 'mt-2 space-y-2 border-t border-gray-200 pt-6 pb-5'
              : 'space-y-2 pb-5'
          }
        >
          {afterQuickHelp && (
            <p className="text-sm text-gray-500">해결되지 않으셨나요?</p>
          )}
          <h2 className="text-lg font-bold text-gray-900">문의 내용을 남겨주세요</h2>
          <EnquiryResponseTimeNotice />
        </div>
        {/* Inputs */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Text Inputs */}
          <div className="grid gap-3 border-b border-gray-200 pb-5 sm:grid-cols-2">
            <TextField
              type="text"
              label="성함"
              placeholder="성함을 입력해주세요."
              className="w-full rounded-lg text-sm font-normal"
              labelClassname="pb-1 text-sm font-medium"
              {...register(EnquiryInputs.Name, { required: true })}
            />
            <TelField
              label="연락처"
              labelClassname="pb-1 text-sm font-medium"
              value={telValue}
              register={register}
              name={EnquiryInputs.Tel}
              className="w-full rounded-lg text-sm font-normal"
              required={true}
            />
          </div>
          <TextField
            type="text"
            label="제목"
            placeholder="제목을 입력해주세요."
            className="w-full rounded-lg text-sm font-normal"
            labelClassname="pb-1 text-sm font-medium"
            {...register(EnquiryInputs.Title, { required: true })}
          />
          {/* Textarea Input */}
          <div>
            <div className="label-col">
              <label
                htmlFor="enquiry-description"
                className="pb-1 text-sm font-medium text-gray-800"
              >
                문의 내용
              </label>
            </div>
            <div className="relative">
              <textarea
                id="enquiry-description"
                placeholder="문의 내용을 입력해주세요."
                className="textfield h-40 w-full rounded-lg py-3 text-sm font-normal leading-6"
                {...register(EnquiryInputs.Description, { required: true })}
              />
            </div>
          </div>
          {/* File Input */}
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-1 text-sm">
              <p className="font-medium text-gray-700">
                첨부파일 <span className="font-normal text-gray-400">(선택)</span>
              </p>
              <span className="text-xs text-gray-500">이미지 · 최대 50MB</span>
            </div>
            <div className="relative">
              <FileUploader
                className="overflow-hidden rounded-lg"
                placeholder="필요한 이미지를 첨부해주세요"
                multiple
                accept={ALLOWED_FILE_FORMAT}
                {...register(EnquiryInputs.Files)}
              />
            </div>
          </div>
          {/* Submit Button */}
          <div className="pt-1">
            <Button
              type="submit"
              className="button-rectangle w-full rounded-xl bg-gray-900 py-3 text-base text-white hover:bg-gray-800"
            >
              문의 남기기
            </Button>
          </div>
        </form>
      </div>
      <LoadingModal open={processingEnquiry} title={'문의 요청중'} />
    </>
  );
};
